import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/** Toast variant determines the accent color + icon. */
export type ToastVariant = 'success' | 'error' | 'info';

export interface Toast {
  id: string;
  variant: ToastVariant;
  title: string;
  body?: string;
  /** Optional URL — when present the toast is clickable. */
  href?: string;
  /** Auto-dismiss delay in ms. Omit for sticky (caller dismisses manually). */
  timeoutMs?: number;
}

interface ToastContextValue {
  toasts: Toast[];
  push: (toast: Omit<Toast, 'id'>) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/** Access the toast API. Throws if called outside ToastProvider. */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

interface ToastProviderProps {
  children: React.ReactNode;
}

/**
 * Global toast provider. Mount once at the app root. Toasts are rendered by
 * `<ToastContainer />` — kept separate so the provider doesn't own DOM.
 */
export function ToastProvider({ children }: ToastProviderProps): React.JSX.Element {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const push = useCallback((toast: Omit<Toast, 'id'>): string => {
    const id = `t-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    setToasts(prev => [...prev, { ...toast, id }]);
    return id;
  }, []);

  useEffect(() => {
    const timers = toasts
      .filter(t => typeof t.timeoutMs === 'number' && t.timeoutMs > 0)
      .map(t => window.setTimeout(() => dismiss(t.id), t.timeoutMs));
    return () => { timers.forEach(clearTimeout); };
  }, [toasts, dismiss]);

  const value = useMemo<ToastContextValue>(() => ({ toasts, push, dismiss }), [toasts, push, dismiss]);

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}
